import { categorizeDataType } from './filterUtils';
import type { FilterOperator } from './filterUtils';
import type { TableExplorerColumn } from '../../core/types/workspaceTypes';

export type CellFilterOperatorChoice = {
  operator: FilterOperator;
  value?: string | number | boolean | Date;
};

/**
 * Pick a filter operator + value based on a cell's value — shared by Table
 * Explorer's "Add to filter" and the Query Editor results grid's "Add to
 * WHERE", so both start from the same condition.
 *
 * - null / undefined → IS NULL (no value)
 * - Array column (Postgres `int[]`, `uuid[]`, …):
 *   - Empty array → array_is_empty
 *   - Non-empty → array_contains seeded with the first element (the only
 *     reasonable default; the user can edit the value or operator after).
 * - FK column with non-null value → lookup (FK row picker default)
 * - everything else → = (exact match), with objects/arrays stringified to JSON
 */
export function pickCellFilterOperator(
  value: unknown,
  column?: Pick<TableExplorerColumn, 'dataType' | 'isForeignKey'>,
): CellFilterOperatorChoice {
  if (value === null || value === undefined) {
    return { operator: 'IS NULL' };
  }

  // Array-typed column: seed array operators directly so the resulting filter
  // emits valid Postgres SQL (rather than `<col> = '[1,2,3]'` which is junk).
  const isArrayColumn = column?.dataType
    ? categorizeDataType(column.dataType) === 'array'
    : false;
  if (isArrayColumn && Array.isArray(value)) {
    if (value.length === 0) return { operator: 'array_is_empty' };
    const firstScalar = value.find(
      (el) =>
        typeof el === 'string' ||
        typeof el === 'number' ||
        typeof el === 'boolean'
    );
    if (firstScalar !== undefined) {
      return {
        operator: 'array_contains',
        value: firstScalar as string | number | boolean,
      };
    }
    // Non-scalar elements (nested objects/arrays) — stringify the first one.
    return { operator: 'array_contains', value: JSON.stringify(value[0]) };
  }

  const baseOperator: FilterOperator = column?.isForeignKey ? 'lookup' : '=';

  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    value instanceof Date
  ) {
    return { operator: baseOperator, value };
  }

  // Objects / arrays — stringify so they fit into a scalar filter value.
  return { operator: baseOperator, value: JSON.stringify(value) };
}
